import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  React.useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-slide-in');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.scroll-animate, .scroll-animate-left, .scroll-animate-right');
    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  return (
    <div className="bg-black min-h-screen">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              About <span className="text-red-500">Swift HandyMen</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Your trusted handyman service provider in Tampa Bay, Brandon, Riverview, Clearwater, and St. Petersburg
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="scroll-animate-left">
              <h2 className="text-4xl font-bold text-white mb-6">
                Locally Owned & <span style={{ color: '#FFD700' }}>Locally Operated</span>
              </h2>
              <p className="text-gray-300 text-lg mb-4">
                Swift HandyMen is a locally owned handyman business serving the Tampa Bay area. We're not just another handyman service – we're your neighbors, committed to providing quality home repairs, installations, and professional handyman services to our community.
              </p>
              <p className="text-gray-300 text-lg mb-4">
                Whether you need a handyman in Tampa, Brandon, Riverview, Clearwater, or St. Petersburg, we bring years of experience and dedication to every job. From minor repairs to major installations, we handle it all with professionalism and care.
              </p>
              <p className="text-gray-300 text-lg">
                As a local Tampa Bay handyman company, we understand the unique needs of Florida homeowners and businesses. We're licensed, insured, and ready to tackle any handyman job – big or small.
              </p>
            </div>
            <div className="scroll-animate-right">
              <img 
                src="https://customer-assets.emergentagent.com/job_swift-rebuild/artifacts/5eihfixu_370d7d14-5146-4ccd-b2ce-3d0f08ef1ccf.png"
                alt="Swift HandyMen team working on deck construction in Tampa Bay"
                className="rounded-lg shadow-2xl shadow-red-600/20"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-12">
            Why Tampa Bay Chooses <span className="text-red-500">Swift HandyMen</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              'Licensed & Insured Handyman Services',
              'Same-Day Service in Tampa Bay Area',
              'Experienced Tampa Handyman Professionals',
              'Transparent $75/Hour Pricing',
              'All Types of Home Repairs & Installations',
              'Serving Tampa, Brandon, Riverview & More'
            ].map((item, index) => (
              <Card key={index} className="scroll-animate bg-black/50 border-red-900/30">
                <CardContent className="p-6">
                  <div className="flex items-start">
                    <CheckCircle2 className="w-6 h-6 text-red-500 mr-3 flex-shrink-0 mt-1" />
                    <p className="text-white font-medium">{item}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-12">
            Handyman Services Across <span style={{ color: '#FFD700' }}>Tampa Bay</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 text-center">
            {[
              'Tampa Handyman',
              'Brandon Handyman',
              'Riverview Handyman',
              'Clearwater Handyman',
              'St. Petersburg Handyman',
              'Temple Terrace Handyman',
              'Plant City Handyman',
              'Valrico Handyman',
              'Seffner Handyman',
              'Westchase Handyman'
            ].map((area, index) => (
              <div key={index} className="scroll-animate bg-gray-900 p-4 rounded-lg border border-red-900/30 hover:border-red-500 transition-colors">
                <p className="text-white font-medium">{area}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
